import { FC, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { MenuItem, Grid } from '@material-ui/core';

import InfoToolTip from '../../../../../modules/Helpers/InfoToolTip';
import { ButtonControlGroup } from '../../../helpers/ButtonControlGroup';
import { UserProfilePrefs } from '../../../../../modules/Helpers/UserProfilePrefs';
import RowTextFieldWithInfo from '../../../../../modules/Farms/Intervention/RowTextFieldWithInfo';
import ReactHookFormSelect from '../../../../../modules/Helpers/ReactHookFormSelect';
import { unitLong } from '../../../../utils/unit-utils';
import { DialogContainer } from '../../CommonDataParts/DialogContainer';
import { useIntl } from '../../../../../../_metronic/i18n/customUseIntl';
import { BaselineDialogProps, FormType } from '../../common';
import { DairyBaseline, DairyManure, LatestCalc3NOP } from '../../../../models/Baseline/DairyBaseline';
import { Maybe } from '../../../../../../graphql/types';

interface DManureFormDialogProps extends BaselineDialogProps {
  // eslint-disable-next-line react/require-default-props
  latestBovaerCalc?: Maybe<LatestCalc3NOP>;
}

const DManureFormDialog: FC<DManureFormDialogProps> = ({
  formVisible,
  itemIndex = 0,
  formType = FormType.Add,
  handleCancel,
  handleSave = handleCancel,
  manureManagementSystems,
  latestBovaerCalc=undefined,
}) => {

  const intl = useIntl();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const userProfile = UserProfilePrefs.getInstance();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  const userUOM = userProfile.getUserUnitPrefs();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const barnOutputMassUnit = userUOM?.unitBarnOutputMass
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    ? unitLong(userUOM.unitBarnOutputMass)
    : 'kg';
  const formTitle = intl.formatMessage(
    { id: 'SUSTELL.PROCESS.DIALOG.STAGE.MANURE.TITLE' },
    { br: ' ' }
  ); // no new line just space needed in Dialog title

  const currResetValue = useRef<DairyManure>();
  const fieldItemPrefix = `stages.${itemIndex}.stageData.manure`;
  const formContext = useFormContext();

  useEffect(() => {
    if (formVisible) {
      currResetValue.current = { ...formContext.getValues(fieldItemPrefix) } as DairyManure; // must 'spread' otherwise just reference
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formVisible]);

  const handleResetClick = () => {
    if (currResetValue.current) {
      const resetObject = { ...formContext.getValues() } as DairyBaseline;
      if (resetObject.stages[itemIndex]?.stageData?.manure) {
        resetObject.stages[itemIndex].stageData.manure = {
          ...currResetValue.current,
        };
        formContext.reset(resetObject, { errors: true });
      }
    }
    if (handleCancel) handleCancel();
  };

  const handleAvgAnimalsChanged = () => {
    // only if 3NOP is checked ask to recalculate
    if (formContext.getValues(`stages.${itemIndex}.stageData.emissions.is3NOPSelected`)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const avgAnimals = formContext.getValues(`stages.${itemIndex}.stageData.manure.averageAnimalsPresent`);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (avgAnimals !== latestBovaerCalc?.input?.averageAnimalsPresent) {
        formContext.setValue(`stages.${itemIndex}.stageData.emissions.supplement3NOP.calculatedResult_recalculate_error`, intl.formatMessage({id: "BOVAER.ERROR.CALCULATE_AGAIN" }))
      } else {
        formContext.setValue(`stages.${itemIndex}.stageData.emissions.supplement3NOP.calculatedResult_recalculate_error`, '');
      }
      (async () => formContext.trigger(`stages.${itemIndex}.stageData.emissions.supplement3NOP`)) ().catch(() => console.log('err'));
    }
  };

  return (
    <DialogContainer
      formVisible={formVisible}
      variant="wide"
      handleClose={handleResetClick}
      iconCode="manureIcon"
      formTitle={formTitle}
    >
      <Grid container direction="row" spacing={3} justifyContent="space-between">
        <Grid item container direction="column" xs={6} spacing={3}>
          <Grid item>
            <RowTextFieldWithInfo
              name={`${fieldItemPrefix  }.averageAnimalsPresent`}
              label={
                `${intl.formatMessage({
                  id: 'BASELINE.FORM.BARN.AVERAGE_ANIMALS_PRESENT',
                })  } *`
              }
              margin="none"
              variant="outlined"
              onChange={handleAvgAnimalsChanged}
              tooltip={intl.formatMessage({
                id: 'BASELINE.FORM.BARN.AVERAGE_ANIMALS_PRESENT.TOOLTIP',
              })}
              disabled={formType === 'view'}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              defaultValue={formContext.getValues(
                `${fieldItemPrefix  }.averageAnimalsPresent`
              )}
            />
          </Grid>
          <Grid item container spacing={1}>
            <Grid item xs={11}>
              <ReactHookFormSelect
                name={`${fieldItemPrefix  }.manureManagementSystem`}
                label={intl.formatMessage({
                  id: 'BASELINE.FORM.BARN.MANURE_MANAGEMENT_SYSTEM',
                })}
                control={formContext.control}
                variant="outlined"
                fullWidth
                disabled={formType === 'view'}
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                defaultValue={formContext.getValues(
                  `${fieldItemPrefix  }.manureManagementSystem`
                )}
              >
                {manureManagementSystems?.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.display_name}
                  </MenuItem>
                ))}
              </ReactHookFormSelect>
            </Grid>
            <Grid item xs={1} className="d-flex">
              <div style={{ paddingTop: '15px', paddingLeft: '7px' }}>
                <InfoToolTip
                  tooltip={
                    <div>
                      {intl.formatMessage(
                        {
                          id: 'BASELINE.FORM.BARN.DAIRY.MANURE_MANAGEMENT_SYSTEM.TOOLTIP',
                        },
                        { br: <br /> }
                      )}
                    </div>
                  }
                />
              </div>
            </Grid>
          </Grid>
          <Grid item style={{ marginTop: '-1.75rem' }}>
            <RowTextFieldWithInfo
              name={`${fieldItemPrefix  }.solidManureStorage`}
              label={`${intl.formatMessage({
                id: 'BASELINE.FORM.BARN.SOLID_MANURE',
              })} *`}
              margin="none"
              variant="outlined"
              tooltip={intl.formatMessage({
                id: 'BASELINE.FORM.BARN.SOLID_MANURE.TOOLTIP',
              })}
              addornment="%"
              disabled={formType === 'view'}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              defaultValue={formContext.getValues(`${fieldItemPrefix  }.solidManureStorage`)}
            />
          </Grid>
        </Grid>
        <Grid item container direction="column" xs={6} spacing={3}>
          <Grid item>
            <RowTextFieldWithInfo
              name={`${fieldItemPrefix  }.slurryManureStorage`}
              label={`${intl.formatMessage({
                id: 'BASELINE.FORM.BARN.SLURRY_MANURE',
              })} *`}
              margin="none"
              variant="outlined"
              tooltip={intl.formatMessage({
                id: 'BASELINE.FORM.BARN.SLURRY_MANURE.TOOLTIP',
              })}
              addornment="%"
              disabled={formType === 'view'}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              defaultValue={formContext.getValues(`${fieldItemPrefix  }.slurryManureStorage`)}
            />
          </Grid>
          <Grid item>
            <RowTextFieldWithInfo
              name={`${fieldItemPrefix  }.strawForBedding`}
              label={intl.formatMessage({
                id: 'BASELINE.FORM.BARN.STRAW_FOR_BEDDING',
              })}
              margin="none"
              variant="outlined"
              tooltip={intl.formatMessage({
                id: 'BASELINE.FORM.BARN.STRAW_FOR_BEDDING.TOOLTIP',
              })}
              addornment={intl.formatMessage(
                { id: 'ADDORNMENT.UNITS.WITH_PER_ANIMAL_SUFIX' },
                { unit: barnOutputMassUnit }
              )}
              disabled={formType === 'view'}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              defaultValue={formContext.getValues(`${fieldItemPrefix  }.strawForBedding`)}
            />
          </Grid>
          <Grid item>
            <RowTextFieldWithInfo
              name={`${fieldItemPrefix  }.sawDust`}
              label={intl.formatMessage({ id: 'BASELINE.FORM.BARN.SAW_DUST' })}
              margin="none"
              variant="outlined"
              tooltip={intl.formatMessage({
                id: 'BASELINE.FORM.BARN.SAW_DUST.TOOLTIP',
              })}
              addornment={intl.formatMessage(
                { id: 'ADDORNMENT.UNITS.WITH_PER_ANIMAL_SUFIX' },
                { unit: barnOutputMassUnit }
              )}
              disabled={formType === 'view'}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              defaultValue={formContext.getValues(`${fieldItemPrefix  }.sawDust`)}
            />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <ButtonControlGroup
            cancelHandler={handleResetClick}
            saveHandler={handleSave}
            saveLabel={intl.formatMessage({ id: 'GENERAL.CONFIRM' })}
          />
        </Grid>
      </Grid>
    </DialogContainer>
  );
};

export default DManureFormDialog;